import { useState, useRef, useEffect } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiArrowLeft } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

const Step2OtpVerificationForm = ({
  isActive = true,
  onNext,
  onBack,
  verifyOtp,
  resendOtp,
  formData,
  onLoginSuccess,
}) => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [resendMessage, setResendMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
        clearInterval(interval);
      }
    }, 1000);
  }, []);

  const handleVerify = async () => {
    if (!isActive) return;
    if (isVerifying) return;
    const otpCode = otp.join("");

    if (otpCode.length < 4) {
      alert("Please enter a valid 4-digit OTP.");
      return;
    }

    setIsVerifying(true);
    try {
      const response = await verifyOtp({
        phone: formData.phoneNumber,
        otp: otpCode,
      });

      if (response.isRegistered) {
        onLoginSuccess();
      } else {
        onNext();
      }
    } catch (err) {
      console.error(err);
      alert("Invalid OTP. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendOtp({ phone: formData.phoneNumber });
      setOtp(["", "", "", ""]);
      inputRefs.current[0]?.focus();
      setResendMessage("OTP resent successfully!");
      setTimeout(() => setResendMessage(""), 3000);
    } catch (err) {
      console.error(err);
      setResendMessage("Failed to resend OTP");
      setTimeout(() => setResendMessage(""), 3000);
    }
  };

  const handleChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      handleVerify();
    }
  };

  return (
    <div className="w-full bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col justify-center items-center p-5 sm:p-7">
      <button
        onClick={onBack}
        className="self-start mb-3 flex items-center gap-2 px-3.5 py-1.5 rounded-md border border-transparent text-gray-700 font-medium select-none 
             hover:text-btn-brandRed hover:bg-btn-brandRed/10 hover:border-btn-brandRed  
             active:scale-95 transition-all duration-200 hover:shadow-sm"
      >
        <FiArrowLeft className="text-[17px]" />
        <span className="text-sm">Back</span>
      </button>

      <div className="text-center mb-5">
        <h1 className="text-2xl lg:text-xl  xl:text-2xl font-semibold text-gray-900 mb-2">
          Verify your Phone Number
        </h1>
        <p className="text-sm text-gray-500">
          We've sent a 4-digit OTP to your mobile
        </p>
      </div>

      <div className="flex justify-center gap-3 mb-5">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength="1"
            autoComplete="one-time-code"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={isVerifying}
            className="w-12 h-12 sm:w-14 sm:h-14 text-center text-gray-900 text-lg border-[1.5px] border-gray-300 rounded-xl outline-none focus:ring-1 focus:ring-btn-brandRed transition-all duration-200"
          />
        ))}
      </div>

      <div className="text-center mb-5">
        <span className="text-sm text-gray-600">Didn't receive code? </span>
        <button
          onClick={handleResend}
          disabled={isVerifying}
          className="text-sm text-btn-brandRed font-semibold hover:text-btn-brandRedHover transition-colors disabled:opacity-60"
        >
          Resend OTP
        </button>
        {resendMessage && (
          <p className="text-xs text-green-600 mt-1">{resendMessage}</p>
        )}
      </div>

      <button
        onClick={handleVerify}
        disabled={isVerifying}
        className="w-full bg-btn-brandRed hover:bg-btn-brandRedHover text-white font-semibold text-[15px] py-3 rounded-full transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-md flex items-center justify-center gap-2"
      >
        {isVerifying && (
          <AiOutlineLoading3Quarters className="animate-spin text-lg" />
        )}
        {isVerifying ? "Verifying..." : "Verify OTP"}
      </button>
    </div>
  );
};

export default Step2OtpVerificationForm;
